import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';

/**
 * Two-point ghost segment used by the line / polygon pickers as the
 * "trailing edge" between the last picked vertex and the cursor.
 *
 * Why a hand-rolled `THREE.Line` instead of drei's `<Line>`: drei's
 * `Line` wraps three-stdlib's `Line2` and renders thick lines as
 * screen-space quads. That works well for stable polylines, but for a
 * 2-point segment whose endpoints change every frame we hit two issues:
 *   1) the bounding sphere on `LineGeometry.setPositions` doesn't
 *      always update reliably, so the line gets frustum-culled and
 *      vanishes;
 *   2) the dashed mode accumulates `lineDistance` along the geometry —
 *      for a tiny segment the dash phase can land entirely inside a gap,
 *      rendering nothing.
 *
 * The native `THREE.Line` with a `LineDashedMaterial` is rock-solid for
 * this case: pixel-thin (1 device pixel) but always visible, dashes are
 * world-units, and `frustumCulled = false` opts out of culling entirely.
 *
 * Geometry, material and the line object are created once and mutated in
 * place per render — earlier versions were allocating and discarding a
 * BufferGeometry on every cursor move, leaking memory.
 */
export const TrailingSegmentLine = ({
  points,
  color,
  dashSize = 0.4,
  gapSize = 0.2,
  opacity = 0.95,
}: {
  points: [THREE.Vector3, THREE.Vector3];
  color: string;
  dashSize?: number;
  gapSize?: number;
  opacity?: number;
}) => {
  // Persistent buffers — six floats for position, two for lineDistance.
  // Created once, mutated per render. R3F disposes via the primitive
  // unmount path; we also explicitly dispose on unmount as a safety net.
  const positionAttr = useRef<THREE.BufferAttribute | null>(null);
  const distanceAttr = useRef<THREE.BufferAttribute | null>(null);

  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new THREE.BufferAttribute(new Float32Array(6), 3);
    const dist = new THREE.BufferAttribute(new Float32Array(2), 1);
    pos.setUsage(THREE.DynamicDrawUsage);
    dist.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', pos);
    g.setAttribute('lineDistance', dist);
    positionAttr.current = pos;
    distanceAttr.current = dist;
    return g;
  }, []);

  const material = useMemo(
    () =>
      new THREE.LineDashedMaterial({
        color,
        dashSize,
        gapSize,
        transparent: true,
        opacity,
        depthTest: false,
      }),
    // Re-create only when visual config changes — color/opacity tweaks are
    // rare in practice. Mutating fields on the live material would also
    // work but allocating once per *config* change is fine and keeps the
    // code simple.
    [color, dashSize, gapSize, opacity],
  );

  const line = useMemo(() => {
    const l = new THREE.Line(geom, material);
    l.frustumCulled = false;
    l.renderOrder = Number.MAX_SAFE_INTEGER - 100;
    return l;
  }, [geom, material]);

  // Refresh the buffer contents on every render (cheap — six floats).
  const pa = positionAttr.current;
  const da = distanceAttr.current;
  if (pa && da) {
    const pArr = pa.array as Float32Array;
    pArr[0] = points[0].x;
    pArr[1] = points[0].y;
    pArr[2] = points[0].z;
    pArr[3] = points[1].x;
    pArr[4] = points[1].y;
    pArr[5] = points[1].z;
    pa.needsUpdate = true;
    const len = points[0].distanceTo(points[1]);
    const dArr = da.array as Float32Array;
    dArr[0] = 0;
    dArr[1] = len;
    da.needsUpdate = true;
  }

  // Dispose on unmount — the primitive itself doesn't dispose its
  // attached geometry/material when removed (R3F only auto-disposes
  // for JSX-defined geometries / materials, not primitive-attached
  // ones), so we own that lifecycle here.
  useEffect(() => {
    return () => {
      geom.dispose();
      material.dispose();
    };
  }, [geom, material]);

  return <primitive object={line} />;
};
